// SambandhTaalMelTab.tsx
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Heart } from "lucide-react";
import AstriaIndiaV2Shell, { SHELL_COLORS_INDIA_V2 } from "../AstriaIndiaV2Shell";
import {
  AstriaIndiaV2ResultCard,
  AstriaIndiaV2FieldBlock,
  AstriaIndiaV2HighlightBadge,
  AstriaIndiaV2SignatureLine,
  IndiaV2LoadingState,
  IndiaV2ErrorState,
  IndiaV2ChatCta,
} from "../AstriaIndiaV2Card";
import {
  WizardProgress,
  WizardStep,
  WizardNav,
  WizardDropdown,
  WizardSlider,
  WizardChips,
  WizardMultiSelect,
  WizardTextInput,
} from "../AstriaIndiaV2Wizard";
import { useAstriaIndiaV2Tab } from "../useAstriaIndiaV2Tab";
import { AstriaIndiaV2SambandhData } from "../../../../contexts/ChatContext";
import { useIndiaV2Language } from "../../../../utils/India/astriaIndiaV2Language";
import { ASTRIA_INDIA_V2_COPY } from "../../../../utils/India/astriaIndiaV2Copy";

// One shared category, one subcategory per tab — both names must match the
// DB records exactly. The category itself is chosen at runtime by the
// version toggle below ("Astria India V2" vs "Astria India V3") so both
// engines' responses can be compared without leaving the page.
const SUBCATEGORY_NAME = "Sambandh Taal Mel";

const TOTAL_STEPS = 4;

const SambandhTaalMelTab: React.FC = () => {
  // Which engine to call is decided ONLY by which sidebar entry the user
  // clicked ("Astria India V2" vs "Astria India V3" — see
  // Sidebar.tsx/TesterHome.tsx's "?v=3" query flag) and never changes for
  // the lifetime of this page — no in-page switch.
  const [searchParams] = useSearchParams();
  const version: "v2" | "v3" = searchParams.get("v") === "3" ? "v3" : "v2";
  const categoryName = version === "v3" ? "Astria India V3" : "Astria India V2";

  const [lang, setLang] = useIndiaV2Language();
  const copy = ASTRIA_INDIA_V2_COPY[lang];
  const t = copy.sambandh;
  const shared = copy.shared;

  const { loading, error, message, send, reset, goToChat } = useAstriaIndiaV2Tab({
    categoryName,
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [step, setStep] = useState(1);
  const [connectionType, setConnectionType] = useState<string | null>(null);
  const [partnerName, setPartnerName] = useState("");
  const [partnerDob, setPartnerDob] = useState("");
  const [partnerTime, setPartnerTime] = useState("");
  const [partnerPlace, setPartnerPlace] = useState("");
  const [talkFrequency, setTalkFrequency] = useState(50);
  const [tone, setTone] = useState<string | null>(null);
  const [clarity, setClarity] = useState<string[]>([]);

  const started = !!message || loading || !!error;

  // Step 1 collects everything needed for a real two-chart compatibility
  // reading upfront — the backend (astriaIndiaV2Service.js) computes both
  // birth charts from this and is instructed never to ask for these
  // details again on the result page.
  const step1Valid = !!connectionType && !!partnerName.trim() && !!partnerDob.trim();

  const buildMessage = () => {
    const typeLabel =
      t.connectionTypes.find((c) => c.value === connectionType)?.label || "Partner";
    const toneLabel = t.toneOptions.find((o) => o.value === tone)?.label || "Warm";
    const clarityLabels = clarity
      .map((c) => t.clarityOptions.find((o) => o.value === c)?.label)
      .filter(Boolean)
      .join(", ");

    return `Read my Sambandh Taal Mel (relationship rhythm).
Connection type: ${typeLabel}.
Partner's Name: ${partnerName.trim()}.
Partner's Date of Birth: ${partnerDob.trim()}.
Partner's Time of Birth: ${partnerTime.trim() || "Not provided"}.
Partner's Place of Birth: ${partnerPlace.trim() || "Not provided"}.
How often we talk: ${talkFrequency}/100.
Their usual tone with me: ${toneLabel}.
What I want clarity on: ${clarityLabels || "overall rhythm"}.
Please reflect gently on our emotional rhythm.`;
  };

  const handleSubmit = () => {
    send(buildMessage());
  };

  const sambandhData = message?.astriaIndiaV2Data as
    | AstriaIndiaV2SambandhData
    | undefined;

  if (started) {
    return (
      <AstriaIndiaV2Shell
        active="sambandh"
        lang={lang}
        onLangChange={setLang}
        version={version}
      >
        <div style={{ marginBottom: 20 }}>
          <div
            style={{ fontSize: 20, fontWeight: 700, color: "#2E1F45", marginBottom: 4 }}
          >
            {t.tabLabel}
          </div>
          <p style={{ fontSize: 13.5, color: "#8C7DA0" }}>
            {t.tabSublabel} — {t.resultTitle}
          </p>
        </div>

        {loading && <IndiaV2LoadingState label={t.loading} />}

        {!loading && error && (
          <IndiaV2ErrorState message={error} onRetry={handleSubmit} retryLabel={shared.tryAgain} />
        )}

        {!loading && !error && sambandhData && (
          <>
            <AstriaIndiaV2ResultCard
              icon={<Heart className="w-5 h-5" />}
              title={t.resultTitle}
              subtitle={t.tabLabel}
              accent={["#6B3FA0", "#D98CA0"]}
              shareText={`${t.tabLabel}\n\n${sambandhData.rhythm_between}\n\n${sambandhData.connection_path}`}
              shareLabels={shared}
            >
              <AstriaIndiaV2HighlightBadge text={sambandhData.highlight} />
              <AstriaIndiaV2FieldBlock label={t.fields.rhythmBetween} value={sambandhData.rhythm_between} />
              <AstriaIndiaV2FieldBlock label={t.fields.harmonyLevel} value={sambandhData.harmony_level} />
              <AstriaIndiaV2FieldBlock label={t.fields.frictionPoint} value={sambandhData.friction_point} />
              <AstriaIndiaV2FieldBlock label={t.fields.timingAlignment} value={sambandhData.timing_alignment} />
              <AstriaIndiaV2FieldBlock label={t.fields.connectionPath} value={sambandhData.connection_path} />
              <AstriaIndiaV2SignatureLine value={sambandhData.micro_imagery} />
            </AstriaIndiaV2ResultCard>
            <button
              onClick={() => {
                reset();
                setStep(1);
              }}
              className="text-xs font-semibold transition-colors"
              style={{ marginTop: 14, color: "#8C7DA0" }}
            >
              {shared.startNewReading}
            </button>
            <IndiaV2ChatCta onClick={goToChat} label={shared.continueInChat} />
          </>
        )}

        {!loading && !error && !sambandhData && message && (
          <AstriaIndiaV2ResultCard
            icon={<Heart className="w-5 h-5" />}
            title={t.resultTitle}
            subtitle={t.tabLabel}
            accent={["#6B3FA0", "#D98CA0"]}
          >
            <p style={{ fontSize: 15, lineHeight: 1.75, color: "#4A3B5C", whiteSpace: "pre-wrap" }}>
              {message.content}
            </p>
          </AstriaIndiaV2ResultCard>
        )}
      </AstriaIndiaV2Shell>
    );
  }

  return (
    <AstriaIndiaV2Shell
      active="sambandh"
      lang={lang}
      onLangChange={setLang}
      version={version}
    >
      <WizardProgress
        step={step}
        total={TOTAL_STEPS}
        accent="#6B3FA0"
        stepLabel={shared.stepOf}
        percentLabel={shared.percentComplete}
      />

      {step === 1 && (
        <WizardStep
          stepKey="details"
          question={t.step1Question}
          helper={t.step1Helper}
        >
          <div style={{ marginBottom: 18 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: SHELL_COLORS_INDIA_V2.mutedColor,
                marginBottom: 8,
              }}
            >
              {t.connectionTypeLabel}
            </div>
            <WizardDropdown
              options={t.connectionTypes}
              value={connectionType}
              onChange={setConnectionType}
              placeholder={t.connectionTypePlaceholder}
            />
          </div>

          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: SHELL_COLORS_INDIA_V2.mutedColor,
              marginBottom: 8,
            }}
          >
            {t.theirDetailsLabel}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <WizardTextInput
              value={partnerName}
              onChange={setPartnerName}
              placeholder={t.namePlaceholder}
            />
            <WizardTextInput
              value={partnerDob}
              onChange={setPartnerDob}
              placeholder={t.dobPlaceholder}
            />
            <WizardTextInput
              value={partnerTime}
              onChange={setPartnerTime}
              placeholder={t.timePlaceholder}
            />
            <WizardTextInput
              value={partnerPlace}
              onChange={setPartnerPlace}
              placeholder={t.placePlaceholder}
            />
          </div>

          <WizardNav
            showBack={false}
            nextDisabled={!step1Valid}
            onNext={() => setStep(2)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 2 && (
        <WizardStep stepKey="freq" question={t.step2Question}>
          <WizardSlider
            value={talkFrequency}
            onChange={setTalkFrequency}
            lowLabel={t.step2Low}
            highLabel={t.step2High}
          />
          <WizardNav
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 3 && (
        <WizardStep stepKey="tone" question={t.step3Question}>
          <WizardChips options={t.toneOptions} value={tone} onChange={setTone} />
          <WizardNav
            onBack={() => setStep(2)}
            nextDisabled={!tone}
            onNext={() => setStep(4)}
            nextLabel={shared.continueLabel}
            backLabel={shared.back}
          />
        </WizardStep>
      )}

      {step === 4 && (
        <WizardStep
          stepKey="clarity"
          question={t.step4Question}
          helper={t.step4Helper}
        >
          <WizardMultiSelect options={t.clarityOptions} values={clarity} onChange={setClarity} />
          <WizardNav
            onBack={() => setStep(3)}
            nextLabel={t.revealButton}
            onNext={handleSubmit}
            backLabel={shared.back}
          />
        </WizardStep>
      )}
    </AstriaIndiaV2Shell>
  );
};

export default SambandhTaalMelTab;
